import CreateTestSpace from './CreateTestSpace';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  TextField,
  Select,
  DataTable,
  Pagination,
  Spinner,
  Banner,
  ButtonGroup,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface Customer {
  id: string;
  name: string;
  slug: string;
  stripe_subscription_status: string | null;
  stripe_current_period_end: string | null;
  billing_company_name: string | null;
  billing_country: string | null;
  created_at: string;
  user_count: number;
  shop_count: number;
  owner_email: string | null;
  owner_name: string | null;
}

interface CustomersResponse {
  success: boolean;
  customers: Customer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function CustomersList() {
  const navigate = useNavigate();
  const { fetchApi, error } = useApi();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const loadCustomers = useCallback(async () => {
    setLoading(true);

    const params = new URLSearchParams();
    params.append('page', pagination.page.toString());
    params.append('limit', pagination.limit.toString());
    if (status) params.append('status', status);
    if (search) params.append('search', search);

    const response = await fetchApi<CustomersResponse>(`/super-admin/customers?${params.toString()}`);

    if (response?.success) {
      setCustomers(response.customers);
      setPagination(response.pagination);
    }

    setLoading(false);
  }, [fetchApi, pagination.page, status, search]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadCustomers();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'trialing':
        return <Badge tone="info">Trial</Badge>;
      case 'canceled':
        return <Badge tone="critical">Canceled</Badge>;
      case 'unpaid':
        return <Badge tone="warning">Unpaid</Badge>;
      case 'past_due':
        return <Badge tone="warning">Past Due</Badge>;
      default:
        return <Badge tone="attention">No Subscription</Badge>;
    }
  };

  const rows = customers.map((customer) => [
    <div key={customer.id}>
      <Text as="span" fontWeight="semibold">{customer.name}</Text>
      <br />
      <Text as="span" tone="subdued" variant="bodySm">{customer.slug}.whenlyapp.com</Text>
    </div>,
    <div key={`owner-${customer.id}`}>
      <Text as="span">{customer.owner_name || '-'}</Text>
      <br />
      <Text as="span" tone="subdued" variant="bodySm">{customer.owner_email || '-'}</Text>
    </div>,
    getStatusBadge(customer.stripe_subscription_status),
    <InlineStack key={`counts-${customer.id}`} gap="400">
      <Text as="span">{customer.user_count} users</Text>
      <Text as="span">{customer.shop_count} shops</Text>
    </InlineStack>,
    customer.billing_country || '-',
    formatDate(customer.created_at),
    <Button key={`view-${customer.id}`} size="slim" onClick={() => navigate(`/admin/customers/${customer.id}`)}>
      View
    </Button>,
  ]);

  return (
    <Page
      title="Customers"
      subtitle={`${pagination.total} total customers`}
    >
      <Layout>
        <Layout.Section><CreateTestSpace /></Layout.Section>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" wrap={false} blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search"
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by name or subdomain..."
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setSearch('')}
                />
              </div>
              <Select
                label="Subscription Status"
                options={[
                  { label: 'All', value: '' },
                  { label: 'Active', value: 'active' },
                  { label: 'Trialing', value: 'trialing' },
                  { label: 'Canceled', value: 'canceled' },
                ]}
                value={status}
                onChange={setStatus}
              />
              <ButtonGroup>
                <Button onClick={handleSearch} variant="primary">Search</Button>
              </ButtonGroup>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            ) : customers.length === 0 ? (
              <BlockStack gap="200" inlineAlign="center">
                <Text as="p" tone="subdued">No customers found</Text>
              </BlockStack>
            ) : (
              <>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Organization', 'Owner', 'Status', 'Usage', 'Country', 'Created', 'Actions']}
                  rows={rows}
                />
                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    hasPrevious={pagination.page > 1}
                    hasNext={pagination.page < pagination.totalPages}
                    onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  />
                </div>
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
